import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import emailjs from '@emailjs/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import AboutImage from '../images/abouts.jpg';

const AboutUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const emailResponse = await emailjs.send(
        'service_3tx8jli',
        'template_2nq3cmj',
        {
          name: formData.name,
          email: formData.email,
          mobile: formData.phone, // 'phone' को 'mobile' के रूप में मैप करें
          subject: formData.subject,
          message: formData.message,
        },
        '2znub3nsdivNcJHnz'
      );

      console.log('Email sent successfully:', emailResponse.status, emailResponse.text);
      setSuccessMessage('Thank you for your enquiry! We will get back to you soon.');
      setFormData({ name: '', phone: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.error('Error sending email:', err);
      setErrorMessage('Error sending enquiry. Please try again.');
    }
  };

  // Show modal and auto-close after 3 seconds
  React.useEffect(() => {
    if (successMessage || errorMessage) {
      setShowModal(true);
      const timer = setTimeout(() => {
        setShowModal(false);
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  return (
    <div className="about-us-page py-5">
      {/* About the Company Section */}
      <section className="about-company-section py-5">
        <Container>
          <h2 className="text-center mb-5 about-title-a animate__animated animate__fadeInDown">
            About Us
          </h2>
          <Row>
            <Col md={6} className="mb-4">
              <img src={AboutImage} alt="About Our NGO" className="about-image img-fluid" />
            </Col>
            <Col md={6} className="mb-4">
              <div className="about-content">
                <h3 className="about-subtitle">Our Story</h3>
                <p className="about-text">
                  We are committed to promoting free education for youth by establishing schools, colleges, certification courses in skill development, vocational training centres, and technical coaching institutes across India and beyond. Our mission is to empower youth, developing them into skilled individuals who can contribute meaningfully to society. We focus on key areas such as healthcare, environmental protection, information technology, and more. We also organize programs in child education, skill development, domestic violence awareness, women empowerment, and innovation, while supporting government initiatives for all communities. Looking ahead, we aim to establish colleges and innovation centres for those in need, nurturing talent in various fields and driving growth and progress.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission and Vision Section */}
      <section className="mission-vision-section py-5 bg-light">
        <Container>
          <Row>
            <Col md={6} className="mb-4">
              <div className="mission-box p-4 rounded shadow bg-white">
                <h3 className="mission-title text-danger">Our Mission</h3>
                <p className="mission-text">
                  To empower youth by providing free education, skill-building, and vocational training, fostering their growth into responsible citizens who drive positive change in healthcare, environmental sustainability, technology, and beyond.
                </p>
              </div>
            </Col>
            <Col md={6} className="mb-4">
              <div className="vision-box p-4 rounded shadow bg-white">
                <h3 className="vision-title text-danger">Our Vision</h3>
                <p className="vision-text">
                  To build a nation where every individual has access to quality education, essential skills, and equal opportunities—fostering innovation, cultural enrichment, and sustainable development across all communities in India.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Enquiry Form Section */}
      <section className="enquiry-section py-5">
        <Container>
          <h2 className="text-center mb-5 enquiry-title animate__animated animate__fadeInDown">
            Enquire Now
          </h2>
          <Row>
            {/* Left Side: Content */}
            <Col md={6} className="mb-4">
              <div className="enquiry-content p-4 rounded shadow bg-white">
                <h3 className="text-danger mb-4">Why Contact Us?</h3>
                <p className="enquiry-text">
                  We’re here to support you—whether you have questions, wish to contribute, or want to get involved. Reach out to learn more about our work and how you can make a difference.
                </p>
                <p>Here’s how we can help:</p>
                <ul className="enquiry-list">
                  <li>Learn more about our ongoing programs and initiatives.</li>
                  <li>Discover ways to contribute or collaborate.</li>
                  <li>Join us as a volunteer and be part of the impact.</li>
                  <li>Support our cause through donations.</li>
                </ul>
              </div>
            </Col>

            {/* Right Side: Form */}
            <Col md={6} className="mb-4">
              <div className="enquiry-form-box p-4 rounded shadow bg-white">
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formName">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formPhone">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter your phone number"
                          pattern="[0-9]{10}"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formSubject">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control
                          as="select"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select a subject</option>
                          <option value="Donate Enquiry">Donate Enquiry</option>
                          <option value="Add Volunteers">Add Volunteers</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="formMessage">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Your message"
                      required
                    />
                  </Form.Group>

                  <div className="text-center">
                    <Button variant="danger" type="submit" className="enquiry-btn">
                      Submit Enquiry
                    </Button>
                  </div>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Success/Error Modal */}
      <Modal show={showModal} centered>
        <Modal.Body className="text-center">
          {successMessage ? (
            <div className="d-flex flex-column align-items-center">
              <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
              <h4 className="text-success mb-2">Success!</h4>
              <p>{successMessage}</p>
            </div>
          ) : (
            <div className="d-flex flex-column align-items-center">
              <FontAwesomeIcon icon={faTimesCircle} size="3x" className="text-danger mb-3" />
              <h4 className="text-danger mb-2">Error!</h4>
              <p>{errorMessage}</p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AboutUs;